// ./src/utils/dateHelpers.ts
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import duration from "dayjs/plugin/duration";
// Register the app's non-default locales so month names localize in
// formatSmartDateTime (English is dayjs's built-in default).
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/ru";

// Enable plugins
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

export const getScheduleDate = (date?: any) => {
  if (date) return date;

  // Get local date to avoid timezone issues
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const formatDuration = (seconds?: number): string => {
  if (!seconds || seconds <= 0) return "-";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  } else {
    return `${secs}s`;
  }
};

export const formatTime = (date: Date): string => {
  return dayjs(date).format("hh:mm A");
};

/* export const formatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}; */

export const formatDate = (date?: string | Date | null) => {
  if (!date) return "-";

  return dayjs(date).format("MMM DD, YYYY");
};

/** Today as a calendar date string ("YYYY-MM-DD"). */
export const getTodayDate = (): string => {
  return dayjs().format("YYYY-MM-DD");
};

/**
 * Add whole months to a "YYYY-MM-DD" calendar date, returning "YYYY-MM-DD".
 * Returns the input unchanged when the date or month count is missing.
 */
export const addMonthsToDate = (
  dateStr?: string | null,
  months?: number | null
): string => {
  if (!dateStr || !months) {
    return dateStr || "";
  }

  return dayjs(dateStr).add(months, "month").format("YYYY-MM-DD");
};

export const formatDateWeekDay = (date?: string | Date | null) => {
  if (!date) return "-";

  return dayjs(date).format("ddd, MMM DD, YYYY");
};

/**
 * Format time from UTC string, optionally ignoring timezone conversion
 * @param utcTimeString - UTC time string like "1970-01-01T17:00:00.000+00:00"
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats the time as-is without timezone conversion
 */
export const formatTimeFromUTC = (
  utcTimeString?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  if (!utcTimeString) return "-";

  try {
    let time: dayjs.Dayjs;

    if (ignoreTimezone) {
      // Strip timezone info (Z or +00:00) to parse as local time
      // This prevents dayjs from treating it as UTC
      const timeWithoutTZ = utcTimeString
        .replace(/Z$/, "")
        .replace(/[+-]\d{2}:\d{2}$/, "");
      time = dayjs(timeWithoutTZ);
    } else {
      // Parse UTC time and convert to local timezone
      time = dayjs.utc(utcTimeString).local();
    }

    if (!time.isValid()) {
      return "-";
    }

    return time.format(format);
  } catch (error) {
    console.warn("Error formatting time:", error);
    return "-";
  }
};

/**
 * Helper function to format time range (legacy - kept for backwards compatibility)
 * For simple pre-formatted time strings
 */
export const formatTimeRange = (startTime?: string, endTime?: string) => {
  if (!startTime && !endTime) return "-";
  if (!endTime) return startTime || "-";
  if (!startTime) return endTime || "-";
  return `${startTime} - ${endTime}`;
};

export const formatDaysOfWeek = (
  daysOfWeek?: string,
  recurrencePattern?: string
) => {
  // Explicit checks to avoid falsy value issues
  if (
    recurrencePattern === undefined ||
    recurrencePattern === null ||
    recurrencePattern === ""
  ) {
    return "-";
  }

  if (daysOfWeek === undefined || daysOfWeek === null || daysOfWeek === "") {
    return "-";
  }

  const recurrencePatternUpper = recurrencePattern.toUpperCase();

  if (recurrencePatternUpper === "WEEKLY") {
    const dayNames: Record<string, string> = {
      "1": "Mon",
      "2": "Tue",
      "3": "Wed",
      "4": "Thu",
      "5": "Fri",
      "6": "Sat",
      "7": "Sun",
    };

    // Replace all "0" with "7" to make Sunday work naturally in sorting
    const normalizedDaysOfWeek = daysOfWeek.replace(/\b0\b/g, "7");

    // Parse days
    const parsedDays = normalizedDaysOfWeek
      .split(",")
      .map((day) => day.trim())
      .filter((day) => day in dayNames);

    if (parsedDays.length === 0) {
      return "-";
    }

    const daySet = new Set(parsedDays);

    // Check for all days
    if (daySet.size === 7) {
      return "All Days";
    }

    // Check for weekends (Sat=6, Sun=7)
    if (daySet.size === 2 && daySet.has("6") && daySet.has("7")) {
      return "Weekend";
    }

    // Check for weekdays (Mon-Fri = 1-5)
    if (
      daySet.size === 5 &&
      daySet.has("1") &&
      daySet.has("2") &&
      daySet.has("3") &&
      daySet.has("4") &&
      daySet.has("5")
    ) {
      return "Weekday";
    }

    // Sort by day number (now 1-7, so natural sorting works!)
    const sortedDays = Array.from(daySet)
      .map((day) => parseInt(day))
      .sort((a, b) => a - b);

    // Group consecutive days
    const groups: string[] = [];
    let i = 0;

    while (i < sortedDays.length) {
      const rangeStart = i;

      // Find consecutive days
      while (
        i < sortedDays.length - 1 &&
        sortedDays[i + 1] === sortedDays[i] + 1
      ) {
        i++;
      }

      const rangeEnd = i;
      const rangeLength = rangeEnd - rangeStart + 1;

      if (rangeLength >= 2) {
        // Range of 2 or more days
        groups.push(
          `${dayNames[sortedDays[rangeStart].toString()]}-${dayNames[sortedDays[rangeEnd].toString()]
          }`
        );
      } else {
        // Single day
        groups.push(dayNames[sortedDays[rangeStart].toString()]);
      }

      i++;
    }

    return groups.join(", ");
  } else if (recurrencePatternUpper === "MONTHLY") {
    // Parse monthly days
    const days = daysOfWeek
      .split(",")
      .map((day) => parseInt(day.trim()))
      .filter((day) => !isNaN(day) && day >= 1 && day <= 31)
      .sort((a, b) => a - b);

    if (days.length === 0) {
      return "-";
    }

    // Group consecutive days
    const groups: string[] = [];
    let i = 0;

    while (i < days.length) {
      const rangeStart = i;

      // Find consecutive days
      while (i < days.length - 1 && days[i + 1] === days[i] + 1) {
        i++;
      }

      const rangeEnd = i;
      const rangeLength = rangeEnd - rangeStart + 1;

      if (rangeLength >= 2) {
        // Range of 2 or more days
        groups.push(`${days[rangeStart]}-${days[rangeEnd]}`);
      } else {
        // Single day
        groups.push(days[rangeStart].toString());
      }

      i++;
    }

    return groups.join(", ");
  }

  return "-";
};

export type DateFormatType =
  | "relative"
  | "short"
  | "medium"
  | "long"
  | "full"
  | "time"
  | "timeShort"
  | "date"
  | "datetime"
  | "datetime2"
  | "iso";

/**
 * Format a date/time string using dayjs with different preset formats
 * @param dateInput - Date string, Date object, or dayjs instance
 * @param format - Format type or custom format string
 * @param timezone - Optional timezone (defaults to local)
 * @returns Formatted date string
 */
export const formatDateTime = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined,
  format: DateFormatType | string = "medium",
  timezone?: string
): string => {
  if (!dateInput) return "-";

  try {
    let date = dayjs(dateInput);

    // Apply timezone if specified
    if (timezone) {
      date = date.tz(timezone);
    }

    // Check if date is valid
    if (!date.isValid()) {
      return "Invalid Date";
    }

    // Handle preset formats
    switch (format) {
      case "relative":
        return formatRelativeDateTime(date);

      case "short":
        return date.format("M/D/YY");

      case "medium":
        return date.format("MMM D, YYYY");

      case "long":
        return date.format("MMMM D, YYYY");

      case "full":
        return date.format("dddd, MMMM D, YYYY [@] h:mm A");

      case "time":
        return date.format("h:mm A");

      case "timeShort":
        return date.format("HH:mm");

      case "date":
        return date.format("YYYY-MM-DD");

      case "datetime":
        return date.format("MMM D, YYYY [@] h:mm A");

      case "datetime2":
        return date.format(`MMM D, YYYY<br/>h:mm A`);

      case "iso":
        return date.toISOString();

      default:
        // Treat as custom format string
        return date.format(format);
    }
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Compact timestamp for created/updated columns, e.g. "Jul 10 2026 @ 10:00 AM".
 * The year is dropped when the date falls in the current year, giving
 * "Jul 10 @ 10:00 AM". Month names are localized to `locale` (falls back to the
 * built-in English when a locale isn't registered).
 *
 * @param dateInput - the timestamp
 * @param locale    - a language code like "en", "es", "fr", "ru" (i18n.language)
 */
export const formatSmartDateTime = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined,
  locale?: string
): string => {
  if (!dateInput) return "-";

  try {
    let date = dayjs(dateInput);
    if (!date.isValid()) return "Invalid Date";

    if (locale) {
      // dayjs locale codes are the base language ("en-US" -> "en").
      date = date.locale(locale.split("-")[0]);
    }

    const includeYear = date.year() !== dayjs().year();
    const fmt = includeYear
      ? "MMM DD YYYY [@] hh:mm A"
      : "MMM DD [@] hh:mm A";
    return date.format(fmt);
  } catch (error) {
    console.warn("Error formatting date:", error);
    return "Invalid Date";
  }
};

/**
 * Format date/time in a user-friendly relative format.
 * This is the unified relative time formatting used across the app.
 * 
 * Rules:
 * - < 1 minute ago: "Just now"
 * - < 2 hours ago: "X minutes ago" or "X hour(s) ago"
 * - Today (but > 2 hours): "Today, 2:30 PM"
 * - Yesterday: "Yesterday, 10:15 AM"
 * - Older (same year): "Jan 15, 3:45 PM"
 * - Older (different year): "Jan 15, 2024, 3:45 PM"
 * 
 * @param date - dayjs instance to format
 * @returns Formatted relative date/time string
 */
const formatRelativeDateTime = (date: dayjs.Dayjs): string => {
  const now = dayjs();
  const diffMinutes = now.diff(date, "minute");
  const diffHours = now.diff(date, "hour");

  // Less than 1 minute ago
  if (diffMinutes < 1) {
    return "Just now";
  }

  // Less than 2 hours ago - use relative time
  if (diffHours < 2) {
    if (diffMinutes < 60) {
      return diffMinutes === 1 ? "1 minute ago" : `${diffMinutes} minutes ago`;
    }
    return diffHours === 1 ? "1 hour ago" : `${diffHours} hours ago`;
  }

  const startOfToday = now.startOf("day");
  const startOfYesterday = startOfToday.subtract(1, "day");
  const timeFormat = date.format("h:mm A");

  // Today (but more than 2 hours ago)
  if (date.isAfter(startOfToday)) {
    return `Today, ${timeFormat}`;
  }

  // Yesterday
  if (date.isAfter(startOfYesterday)) {
    return `Yesterday, ${timeFormat}`;
  }

  // Older - check if same year
  if (date.year() === now.year()) {
    return date.format("MMM D, h:mm A");
  }

  // Different year
  return date.format("MMM D, YYYY, h:mm A");
};

/**
 * Get relative time from now (e.g., "2 hours ago", "in 3 days")
 * @deprecated Use formatDateTime(date, "relative") instead for consistent formatting
 */
export const getRelativeTime = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): string => {
  return formatDateTime(dateInput, "relative");
};

/**
 * Check if a date is today
 */
export const isToday = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isSame(dayjs(), "day");
};

/**
 * Check if a date is in the past
 */
export const isPast = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isBefore(dayjs());
};

/**
 * Check if a date is in the future
 */
export const isFuture = (
  dateInput: string | Date | dayjs.Dayjs | null | undefined
): boolean => {
  if (!dateInput) return false;
  const date = dayjs(dateInput);
  return date.isValid() && date.isAfter(dayjs());
};

/**
 * Get duration between two dates in human readable format
 * Returns compact format like "1d 2h 30m"
 */
export const getDuration = (
  startDate: string | Date | dayjs.Dayjs,
  endDate: string | Date | dayjs.Dayjs
): string => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return "Invalid dates";
  }

  const duration = end.diff(start);
  const durationObj = dayjs.duration(duration);

  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else {
    return `${minutes}m`;
  }
};

/**
 * Format duration between two dates in natural language
 * Returns null if dates are invalid, zero, or negative duration
 * 
 * @example
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T08:45:00") // "45 min"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T09:00:00") // "1 hour"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-01T10:30:00") // "2 hours 30 min"
 * formatDurationBetween("2025-01-01T08:00:00", "2025-01-02T10:00:00") // "1 day 2 hours"
 */
export const formatDurationBetween = (
  startDate: string | Date | dayjs.Dayjs | null | undefined,
  endDate: string | Date | dayjs.Dayjs | null | undefined
): string | null => {
  if (!startDate || !endDate) return null;

  const start = dayjs(startDate);
  const end = dayjs(endDate);

  if (!start.isValid() || !end.isValid()) {
    return null;
  }

  const diffMs = end.diff(start);

  // Return null for zero or negative duration
  if (diffMs <= 0) return null;

  const durationObj = dayjs.duration(diffMs);
  const totalMinutes = Math.floor(durationObj.asMinutes());

  if (totalMinutes === 0) return null;

  const days = Math.floor(durationObj.asDays());
  const hours = durationObj.hours();
  const minutes = durationObj.minutes();

  // Build parts array for natural formatting
  const parts: string[] = [];

  if (days > 0) {
    parts.push(days === 1 ? "1 day" : `${days} days`);
  }

  if (hours > 0) {
    parts.push(hours === 1 ? "1 hour" : `${hours} hours`);
  }

  if (minutes > 0 && days === 0) {
    // Only show minutes if less than a day
    parts.push(`${minutes} min`);
  }

  // If we have days but no hours and no minutes shown, still valid
  if (parts.length === 0) {
    return null;
  }

  // Join with space (e.g., "2 hours 30 min" or "1 day 3 hours")
  return parts.join(" ");
};

/**
 * Calculate distance between two odometer readings
 * Returns null if either value is missing or result is not positive
 */
export const calculateOdometerDistance = (
  prevOdometer: number | undefined | null,
  currentOdometer: number | undefined | null
): number | null => {
  if (
    prevOdometer === undefined ||
    prevOdometer === null ||
    currentOdometer === undefined ||
    currentOdometer === null
  ) {
    return null;
  }

  const distance = currentOdometer - prevOdometer;
  return distance > 0 ? distance : null;
};

/**
 * Check if a date matches the recurrence pattern
 */
export const matchesRecurrence = (
  checkDate: dayjs.Dayjs,
  recurrencePattern?: string,
  recurrenceDays?: string
): boolean => {
  if (!recurrencePattern || recurrencePattern === "NONE") {
    return true;
  }

  if (!recurrenceDays) {
    return true;
  }

  const days = recurrenceDays.split(",").map((d) => parseInt(d.trim(), 10));

  switch (recurrencePattern) {
    case "WEEKLY": {
      // For weekly: 0=Sunday, 1=Monday, ..., 6=Saturday
      let dayOfWeek: number = checkDate.day();

      if (dayOfWeek == 0) {
        dayOfWeek = 7;
      }

      return days.includes(dayOfWeek);
    }

    case "MONTHLY": {
      // For monthly: 1-31 days of month
      const dayOfMonth = checkDate.date();

      return days.includes(dayOfMonth);
    }

    case "YEARLY": {
      // For yearly recurrenceDays is a day in YYYY-MM-DD
      const date = dayjs(recurrenceDays);

      return date.isSame(checkDate);
    }

    default:
      return true;
  }
};

/**
 * Format UTC date string to local date (date only, no time)
 * Converts "2025-10-01T06:00:00.000+00:00" to local date format
 */
export const formatUTCDateToLocal = (
  utcDateString?: string | null,
  format: string = "MMM D, YYYY"
): string => {
  if (!utcDateString) return "-";

  try {
    // Parse UTC date and convert to local
    const localDate = dayjs.utc(utcDateString).local();

    if (!localDate.isValid()) {
      return "-";
    }

    return localDate.format(format);
  } catch (error) {
    console.warn("Error formatting UTC date:", error);
    return "-";
  }
};

/**
 * Get current local date/time as string for DateTimePicker
 */
export const getCurrentLocalDateTimeString = (): string => {
  return dayjs().format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Format UTC time string to local time (time only, no date)
 * Converts "1970-01-01T17:00:00.000+00:00" to local time format
 * @param utcTimeString - UTC time string
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats the time as-is without timezone conversion
 */
export const formatUTCTimeToLocal = (
  utcTimeString?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  return formatTimeFromUTC(utcTimeString, format, ignoreTimezone);
};

/**
 * Format date range from UTC dates with smart formatting
 * Returns formatted string like:
 * - "Oct 10 - 20, 2025" (same month and year)
 * - "Oct 10 - Nov 5, 2025" (same year, different months)
 * - "Dec 20, 2024 - Jan 5, 2025" (different years)
 * - "Oct 1, 2025" (no end date or same date)
 */
export const formatUTCDateRange = (
  startDate?: string | null,
  endDate?: string | null
): string => {
  if (!startDate && !endDate) return "-";

  try {
    const startLocal = startDate ? dayjs.utc(startDate).local() : null;
    const endLocal = endDate ? dayjs.utc(endDate).local() : null;

    // If only start date exists
    if (startLocal && !endLocal) {
      return startLocal.format("MMM D, YYYY");
    }

    // If only end date exists
    if (!startLocal && endLocal) {
      return `Until ${endLocal.format("MMM D, YYYY")}`;
    }

    // Both dates exist
    if (startLocal && endLocal) {
      // Check if dates are invalid
      if (!startLocal.isValid() || !endLocal.isValid()) {
        return "-";
      }

      // If same date
      if (startLocal.isSame(endLocal, "day")) {
        return startLocal.format("MMM D, YYYY");
      }

      const sameYear = startLocal.year() === endLocal.year();
      const sameMonth = sameYear && startLocal.month() === endLocal.month();

      // Same month and year: "Oct 10 - 20, 2025"
      if (sameMonth) {
        return `${startLocal.format("MMM D")} - ${endLocal.format("D, YYYY")}`;
      }

      // Same year, different months: "Oct 10 - Nov 5, 2025"
      if (sameYear) {
        return `${startLocal.format("MMM D")} - ${endLocal.format(
          "MMM D, YYYY"
        )}`;
      }

      // Different years: "Dec 20, 2024 - Jan 5, 2025"
      return `${startLocal.format("MMM D, YYYY")} - ${endLocal.format(
        "MMM D, YYYY"
      )}`;
    }

    return "-";
  } catch (error) {
    console.warn("Error formatting UTC date range:", error);
    return "-";
  }
};

/**
 * Format time range from UTC times
 * Returns formatted string like "9:00 AM - 5:00 PM" or "9:00 AM" if no end time
 * @param startTime - UTC start time string
 * @param endTime - UTC end time string
 * @param format - Time format string
 * @param ignoreTimezone - If true, treats times as-is without timezone conversion
 */
export const formatUTCTimeRange = (
  startTime?: string | null,
  endTime?: string | null,
  format: string = "h:mm A",
  ignoreTimezone: boolean = false
): string => {
  const start = formatTimeFromUTC(startTime, format, ignoreTimezone);
  const end = formatTimeFromUTC(endTime, format, ignoreTimezone);

  if (start === "-" && end === "-") return "-";
  if (end === "-") return start;
  if (start === "-") return `Until ${end}`;
  if (start === end) return start;

  return `${start} - ${end}`;
};

/**
 * Convert local time string from DateTimePicker to UTC ISO string for API
 */
export const localStringToUtc = (
  localDateString: string | null
): string | null => {
  if (!localDateString) return null;
  try {
    const localDate = dayjs(localDateString);
    if (!localDate.isValid()) return null;
    return localDate.utc().toISOString();
  } catch {
    return null;
  }
};

/**
 * Convert UTC date string from API to local time string for DateTimePicker
 * DateTimePicker expects format like "YYYY-MM-DD HH:mm"
 */
export const utcToLocalString = (
  utcDateString: string | null | undefined
): string | null => {
  if (!utcDateString) return null;
  try {
    // Parse as UTC, convert to local, format for DateTimePicker
    const localDate = dayjs.utc(utcDateString).local();
    if (!localDate.isValid()) return null;
    return localDate.format("YYYY-MM-DD HH:mm");
  } catch {
    return null;
  }
};

/**
 * An EVENT's date or time as the host enters it: a picker's wall-clock text ("YYYY-MM-DD HH:mm[:ss]", or "YYYY-MM-DD" for
 * an all-day date) read in the EVENT's time zone → the UTC ISO the API stores. (2026-10-04: the editors read it in the
 * host BROWSER's zone while every reader — the public site, the door, the time rules — reads it in the event's, so a
 * host in Berlin making an all-day New York event showed it a day early, and a timed one six hours early.)
 * ⚠ No zone → the browser's (what `localStringToUtc` does) — never a guess.
 */
export const zonedStringToUtc = (value: string | null | undefined, tz: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  try {
    const parsed = tz ? dayjs.tz(String(value).trim(), tz) : dayjs(value);

    return parsed.isValid() ? parsed.utc().toISOString() : null;
  } catch {
    // ⚠ A zone this browser cannot read (the API stores any text) reads as UTC — as the server does — never as "no
    // value": returning null here emptied every saved window on such an event, and an untouched Save wrote the null.
    const parsed = dayjs.utc(String(value).trim());

    return parsed.isValid() ? parsed.toISOString() : null;
  }
};

/**
 * The other way: a stored UTC instant → the picker's wall-clock text in the EVENT's time zone ("YYYY-MM-DD" for an
 * all-day date, else "YYYY-MM-DD HH:mm:ss"). ⚠ No zone → the browser's.
 */
export const utcToZonedString = (iso: string | null | undefined, tz: string | null | undefined, dateOnly: boolean): string | null => {
  if (!iso) {
    return null;
  }

  try {
    const instant = dayjs.utc(iso);

    if (!instant.isValid()) {
      return null;
    }

    return (tz ? instant.tz(tz) : instant.local()).format(dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss');
  } catch {
    // An unreadable zone reads as UTC (as the server, and `zonedStringToUtc`, do) — so a load and its save still agree.
    const instant = dayjs.utc(iso);

    return instant.isValid() ? instant.format(dateOnly ? 'YYYY-MM-DD' : 'YYYY-MM-DD HH:mm:ss') : null;
  }
};

/**
 * Whether this browser keeps a different time zone from `tz` — the editors then say whose times the pickers show. Two
 * zones are the same only when their clocks agree all year (checked in January AND July): Phoenix and Denver agree in
 * winter and not in summer.
 */
export const browserZoneDiffers = (tz: string | null | undefined): boolean => {
  if (!tz) {
    return false;
  }

  try {
    const browser = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (browser === tz) {
      return false;
    }

    const year = dayjs().year();

    return [`${year}-01-15T12:00:00Z`, `${year}-07-15T12:00:00Z`].some(
      (at) => dayjs.utc(at).tz(browser).utcOffset() !== dayjs.utc(at).tz(tz).utcOffset(),
    );
  } catch {
    return false;
  }
};

/** Today's date ("YYYY-MM-DD") in `tz`; undefined without one (the caller's own "today" then applies). */
export const todayIn = (tz: string | null | undefined): string | undefined => {
  if (!tz) {
    return undefined;
  }

  try {
    return dayjs().tz(tz).format('YYYY-MM-DD');
  } catch {
    return undefined;
  }
};

/** The calendar date ("YYYY-MM-DD") an instant falls on in `tz`; UTC when there is no zone or it cannot be read. */
const dayInZone = (at: dayjs.Dayjs, tz: string | null | undefined): string => {
  if (tz) {
    try {
      return at.tz(tz).format('YYYY-MM-DD');
    } catch {
      // An unknown zone reads as UTC — as the server does.
    }
  }

  return at.utc().format('YYYY-MM-DD');
};

/**
 * ROADMAP A2e #100, #137 — when an RSVP / ticket deadline CLOSES, in ms: the deadline itself. #137 (owner) made it a
 * MOMENT — a date and a time, no later than the event ends; a day-shaped one ("N days before each date", a deadline
 * saved before #137) is stored as that day's last second. ⚠ The server's `deadlineClosesAt` (eventsmagic-api
 * boundary/eventClock.ts) is the same rule and refuses from that moment: the site must not show Buy / RSVP a moment
 * longer, nor hide it a moment sooner. Null when there is no (readable) deadline.
 */
export const deadlineClosesAt = (deadline: string | null | undefined): number | null => {
  const at = deadline ? dayjs.utc(deadline) : null;

  return at && at.isValid() ? at.valueOf() : null;
};

/** Whole calendar days from `nowMs` to a deadline's day in `tz` — 0 on the last day itself (negative once it is past). */
export const calendarDaysUntil = (deadline: string, nowMs: number, tz: string | null | undefined): number => {
  const last = dayInZone(dayjs.utc(deadline), tz);
  const today = dayInZone(dayjs.utc(nowMs), tz);

  return dayjs.utc(last).diff(dayjs.utc(today), 'day');
};

/**
 * Convert date strings (YYYY-MM-DD) to UTC ISO strings for API calls.
 * - dateFrom: start of day (00:00:00) in user's local timezone → UTC
 * - dateTo: end of day (23:59:59.999) in user's local timezone → UTC
 */
export const toApiDateRange = (
  dateFrom: string | null,
  dateTo: string | null
): { dateFrom: string | null; dateTo: string | null } => {
  return {
    dateFrom: dateFrom
      ? dayjs(dateFrom).startOf("day").utc().toISOString()
      : null,
    dateTo: dateTo ? dayjs(dateTo).endOf("day").utc().toISOString() : null,
  };
};

/**
 * Format short date for compact display
 * Returns "Oct 1" or "Oct 1, 2025" if different year
 */
export const formatUTCDateShort = (utcDateString?: string | null): string => {
  if (!utcDateString) return "-";

  try {
    const localDate = dayjs.utc(utcDateString).local();

    if (!localDate.isValid()) {
      return "-";
    }

    // If same year, show month and day only
    if (localDate.year() === dayjs().year()) {
      return localDate.format("MMM D");
    }

    // Different year, show year too
    return localDate.format("MMM D, YYYY");
  } catch (error) {
    console.warn("Error formatting UTC date short:", error);
    return "-";
  }
};

// Helper functions using dayjs
export const apiDateStringToDate = (
  dateString: string | undefined
): Date | null => {
  if (!dateString) return null;
  const parsed = dayjs(dateString, "YYYY-MM-DD", true);
  return parsed.isValid() ? parsed.toDate() : null;
};

export const dateToApiDateString = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  return dayjs(date).format("YYYY-MM-DD");
};

/**
 * Convert API time format to form time format (HH:mm)
 * API format: "1970-01-01T17:00:00.000Z" or legacy "10:00" or "22:00"
 * Form format: "17:00" (24-hour HH:mm)
 */
export const apiTimeToFormTime = (apiTime: string | undefined): string => {
  if (!apiTime) return "";

  // Check if it's the new ISO format with timezone
  if (apiTime.includes("T") && apiTime.includes("Z")) {
    // Strip the Z to prevent timezone conversion
    const timeWithoutTZ = apiTime
      .replace(/Z$/, "")
      .replace(/[+-]\d{2}:\d{2}$/, "");
    const parsed = dayjs(timeWithoutTZ);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }
  }

  // Legacy format or already in HH:mm format
  // Try to parse as time
  const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
  for (const format of formats) {
    const parsed = dayjs(apiTime, format, true);
    if (parsed.isValid()) {
      return parsed.format("HH:mm");
    }
  }

  return apiTime; // Return as-is if can't parse
};

/**
 * Convert form time format to API time format
 * Form format: "17:00" (24-hour HH:mm from TimeInput)
 * API format: "1970-01-01T17:00:00.000Z"
 * IMPORTANT: Does NOT convert timezone - just formats as UTC string
 */
export const formTimeToApiTime = (formTime: string): string => {
  if (!formTime) return "";

  // Parse the time (TimeInput gives us HH:mm format)
  const parsed = dayjs(formTime, "HH:mm", true);
  if (!parsed.isValid()) {
    // Try other formats just in case
    const formats = ["H:mm", "hh:mm A", "h:mm A"];
    for (const format of formats) {
      const attemptParsed = dayjs(formTime, format, true);
      if (attemptParsed.isValid()) {
        // Create UTC ISO string WITHOUT timezone conversion
        const hour = String(attemptParsed.hour()).padStart(2, "0");
        const minute = String(attemptParsed.minute()).padStart(2, "0");
        return `1970-01-01T${hour}:${minute}:00.000Z`;
      }
    }
    return formTime; // Return as-is if can't parse
  }

  // Create UTC ISO string WITHOUT timezone conversion
  // Just format the time values directly into the ISO string
  const hour = String(parsed.hour()).padStart(2, "0");
  const minute = String(parsed.minute()).padStart(2, "0");
  return `1970-01-01T${hour}:${minute}:00.000Z`;
};

// Helper function to validate time format (HH:mm from TimeInput)
export const isValidTimeFormat = (timeString: string): boolean => {
  if (!timeString) return false;

  // TimeInput primarily outputs HH:mm format
  const formats = [
    "HH:mm", // 24-hour format
    "H:mm", // 24-hour format without leading zero
    "hh:mm A", // 12-hour format with AM/PM
    "h:mm A", // 12-hour format without leading zero
  ];

  return formats.some((format) => dayjs(timeString, format, true).isValid());
};

export const compareTimeStrings = (
  startTime: string,
  endTime: string
): boolean => {
  if (!isValidTimeFormat(startTime) || !isValidTimeFormat(endTime)) {
    return false;
  }

  // Parse times using dayjs with multiple format support
  const parseTime = (timeStr: string) => {
    const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
    for (const format of formats) {
      const parsed = dayjs(timeStr, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }
    return null;
  };

  const startParsed = parseTime(startTime);
  const endParsed = parseTime(endTime);

  if (!startParsed || !endParsed) return false;

  return endParsed.isAfter(startParsed);
};


// Add this type near the top with other types
export type PeriodComparisonType =
  | "previous"
  | "previousMonth"
  | "sameMonthLastYear"
  | "custom"
  | "none";


// ============================================
// Event date/time helpers (wizard + editors)
// ============================================

// Canonical value formats we hand back to Mantine's date inputs.
const DATETIME_VALUE_FMT = "YYYY-MM-DD HH:mm:ss";
const DATE_VALUE_FMT = "YYYY-MM-DD";

/**
 * Whether the given locale (default: the browser/OS locale) uses a 12-hour clock
 * with AM/PM. Drives the time picker's format so North-American users get AM/PM
 * while 24-hour locales get 24-hour input — without hardcoding a region.
 */
export const usesTwelveHourClock = (locale?: string): boolean => {
  try {
    const resolved = new Intl.DateTimeFormat(locale || undefined, {
      hour: "numeric",
    }).resolvedOptions();
    if (typeof resolved.hour12 === "boolean") {
      return resolved.hour12;
    }
    // Fallback: format an afternoon hour and look for an AM/PM marker.
    const sample = new Intl.DateTimeFormat(locale || undefined, {
      hour: "numeric",
    }).format(new Date(2020, 0, 1, 13, 0, 0));
    return /\b(am|pm)\b/i.test(sample);
  } catch {
    return true;
  }
};

/** The Mantine TimePicker format ('12h' | '24h') for the given/browser locale. */
export const clockFormat = (locale?: string): "12h" | "24h" =>
  usesTwelveHourClock(locale) ? "12h" : "24h";

/**
 * Add minutes to a picker value string, returning it in the same shape the
 * picker expects ("YYYY-MM-DD HH:mm:ss", or "YYYY-MM-DD" when allDay).
 */
export const addMinutesToDateTime = (
  value: string | null | undefined,
  minutes: number,
  allDay = false
): string | null => {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.add(minutes, "minute").format(allDay ? DATE_VALUE_FMT : DATETIME_VALUE_FMT);
};

/**
 * Re-anchor an end value so the duration is preserved when the start moves.
 * newEnd = oldEnd + (newStart - oldStart). Returns the existing end unchanged
 * when any input is missing.
 */
export const shiftEndKeepingDuration = (
  oldStart: string | null | undefined,
  oldEnd: string | null | undefined,
  newStart: string | null | undefined,
  allDay = false
): string | null => {
  if (!oldEnd) return null;
  if (!oldStart || !newStart) return oldEnd;
  const deltaMs = dayjs(newStart).diff(dayjs(oldStart));
  return dayjs(oldEnd).add(deltaMs, "millisecond").format(allDay ? DATE_VALUE_FMT : DATETIME_VALUE_FMT);
};

/**
 * Time-only ("HH:mm") counterpart of shiftEndKeepingDuration: re-anchor an end
 * TIME so the gap to the start is preserved when the start time moves. Wraps
 * within the day (an overnight end stays valid). Returns the existing end
 * unchanged when any input is missing/invalid, or "" when there is no end.
 */
export const shiftEndTimeKeepingDuration = (
  oldStart: string | null | undefined,
  oldEnd: string | null | undefined,
  newStart: string | null | undefined
): string => {
  if (!oldEnd) return "";
  if (!oldStart || !newStart) return oldEnd;

  const toMin = (t: string): number | null => {
    const [h, m] = t.split(":").map((n) => Number(n));
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const os = toMin(oldStart);
  const oe = toMin(oldEnd);
  const ns = toMin(newStart);
  if (os === null || oe === null || ns === null) return oldEnd;

  const v = (((oe + (ns - os)) % 1440) + 1440) % 1440;
  return `${String(Math.floor(v / 60)).padStart(2, "0")}:${String(v % 60).padStart(2, "0")}`;
};

/**
 * THE start/end rule for every form with a range (owner, 2026-09-30: "if the form has start and end date/time and user
 * changes start time then similarly offset the end time and if needed user will adjust end time"). Moving the start
 * moves the end by the same amount, so the length is kept; an end that was never set stays unset.
 *
 * ⚠ The end comes back in the SAME SHAPE it was in — "YYYY-MM-DD", a picker's "YYYY-MM-DD HH:mm[:ss]" (space or "T"), or
 *   an ISO instant with a zone ("…Z" / "…+02:00") — because forms here hold all three, and handing a date-only field a
 *   date-time (or a UTC form a local string) is a quiet bug of its own. A time of day ("HH:mm") wraps within the day.
 */
export const moveEndWithStart = (
  oldStart: string | null | undefined,
  oldEnd: string | null | undefined,
  newStart: string | null | undefined
): string | null => {
  if (!oldEnd) {
    return oldEnd ?? null;
  }

  if (!oldStart || !newStart) {
    return oldEnd;
  }

  if (/^\d{1,2}:\d{2}$/.test(oldEnd)) {
    return shiftEndTimeKeepingDuration(oldStart, oldEnd, newStart);
  }

  const s0 = dayjs(oldStart);
  const e0 = dayjs(oldEnd);
  const s1 = dayjs(newStart);

  if (!s0.isValid() || !e0.isValid() || !s1.isValid()) {
    return oldEnd;
  }

  const moved = e0.add(s1.diff(s0), "millisecond");

  if (/^\d{4}-\d{2}-\d{2}$/.test(oldEnd)) {
    return moved.format("YYYY-MM-DD");
  }

  if (/(Z|[+-]\d{2}:?\d{2})$/.test(oldEnd)) {
    return moved.toISOString();
  }

  const sep = oldEnd.charAt(10) === "T" ? "T" : " ";
  const withSeconds = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(oldEnd);

  return moved.format(`YYYY-MM-DD[${sep}]HH:mm${withSeconds ? ":ss" : ""}`);
};

/**
 * The value a NEW item in a sequence starts at: where the previous one ENDED (owner, 2026-09-30: "when user adds several
 * time slots … auto-calculate next start time as end of previous"). The latest end among `items`, or null when none has
 * one. Compared as instants, so mixed shapes sort right.
 */
export const latestEnd = <T>(items: readonly T[], endOf: (item: T) => string | null | undefined): string | null => {
  let best: string | null = null;

  for (const item of items) {
    const end = endOf(item);

    if (end && dayjs(end).isValid() && (!best || dayjs(end).isAfter(dayjs(best)))) {
      best = end;
    }
  }

  return best;
};

/**
 * The earliest value a date picker may take: `minDate` (or today, when the past is not allowed), raised to `notBefore`
 * — the paired START of an end field — when that is later. In the picker's own shape: a date-time field gets the full
 * instant (Mantine then clamps the TIME on that day too), a date-only field the day.
 */
export const pickerFloor = (opts: {
  minDate?: string | null;
  notBefore?: string | null;
  dateOnly?: boolean;
  disallowPast?: boolean;
  today?: string;
}): string | undefined => {
  const base = opts.minDate ?? (opts.disallowPast ? opts.today ?? dayjs().format("YYYY-MM-DD") : undefined);
  const floor =
    opts.notBefore && dayjs(opts.notBefore).isValid()
      ? dayjs(opts.notBefore).format(opts.dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm:ss")
      : undefined;

  return floor && (!base || dayjs(floor).isAfter(dayjs(base))) ? floor : base;
};

/**
 * ROADMAP #137 — the latest value a picker may take: the earlier of `maxDate` and `notAfter` (a paired END — e.g. the
 * event's end above its RSVP deadline). With a time it keeps the time, so the picker clamps the TIME on that last day
 * too (Mantine clamps to a full `maxDate`); date-only, the day. Wall clocks, compared as written.
 */
export const pickerCeiling = (opts: { maxDate?: string | null; notAfter?: string | null; dateOnly?: boolean }): string | undefined => {
  const base = opts.maxDate || undefined;
  const ceiling =
    opts.notAfter && dayjs(opts.notAfter).isValid()
      ? dayjs(opts.notAfter).format(opts.dateOnly ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm:ss")
      : undefined;

  // ⚠ A date-only `maxDate` means its WHOLE day: a ceiling at 9 PM that day is the earlier one (#137 review — read as
  //   midnight, the date-only base won, and every time on the event's own day was refused).
  const baseLast = base && /^\d{4}-\d{2}-\d{2}$/.test(base) ? dayjs(base).endOf("day") : dayjs(base);

  return ceiling && (!base || dayjs(ceiling).isBefore(baseLast)) ? ceiling : base;
};

/**
 * ROADMAP #137 — the wall-clock time a DAY-shaped deadline closes at: that day's last second. The same constant as the
 * API's `DEADLINE_END_OF_DAY` (boundary/eventClock.ts): a deadline is the MOMENT registration closes.
 */
export const DEADLINE_END_OF_DAY = "23:59:59";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * ROADMAP #137 — a deadline as its picker holds it ("YYYY-MM-DD HH:mm:ss"). A DATE-ONLY value — a wizard draft saved
 * before #137, when the field took a day — means "through that day", so it becomes that day's last second, never its
 * first (which would close registration a day early) — and, given the event's `ceiling` (`deadlineCeiling`), no later
 * than the event ends: "through the event's own day" is "until it ends" (#137 review: the server refused it).
 * ⓘ A value WITH a time is the host's own pick and is returned as it is.
 */
export const deadlinePickerValue = (value: string | null | undefined, ceiling?: string | null): string | null => {
  const v = value?.trim() ?? "";

  if (!v) {
    return null;
  }

  if (!DATE_ONLY_RE.test(v)) {
    return v;
  }

  const endOfDay = `${v} ${DEADLINE_END_OF_DAY}`;

  return ceiling && dayjs(ceiling).isValid() && dayjs(endOfDay).isAfter(dayjs(ceiling)) ? ceiling : endOfDay;
};

/**
 * ROADMAP #137 — the LATEST a deadline may be, as a picker wall clock: the event's (the date's) end, else its start; an
 * all-day one (or a date-only value) the last second of that day. Mirrors the API's `latestDeadlineFor`, which refuses
 * anything later. Null when there is nothing to compare with.
 */
export const deadlineCeiling = (when: { startsAt?: string | null; endsAt?: string | null; isAllDay?: boolean | null }): string | null => {
  const last = (when.endsAt || when.startsAt || "").trim();

  if (!last || !dayjs(last).isValid()) {
    return null;
  }

  return when.isAllDay || DATE_ONLY_RE.test(last) ? `${last.slice(0, 10)} ${DEADLINE_END_OF_DAY}` : dayjs(last).format("YYYY-MM-DD HH:mm:ss");
};

/**
 * ROADMAP #137 review — a deadline that sat EXACTLY at the old cap (the event's end — where picking the event's own day
 * lands it) moves with the cap when the dates change: the host meant "until the event ends", and the to-the-minute rule
 * would otherwise refuse their next change to the end. Any other deadline is the host's own choice and stays. Wall clocks.
 */
export const deadlineFollowingCeiling = (
  deadline: string | null | undefined,
  prevCeiling: string | null | undefined,
  nextCeiling: string | null | undefined,
): string | null => {
  if (!deadline || !prevCeiling || !nextCeiling || !dayjs(deadline).isValid() || !dayjs(prevCeiling).isValid()) {
    return deadline ?? null;
  }

  return dayjs(deadline).isSame(dayjs(prevCeiling)) ? nextCeiling : deadline;
};

/** True when a picker's value is later than its ceiling (`pickerCeiling`) — to the second, or the day when date-only. */
export const isAfterCeiling = (value: string | null | undefined, ceiling: string | null | undefined, dateOnly = false): boolean => {
  if (!value || !ceiling || !dayjs(value).isValid() || !dayjs(ceiling).isValid()) {
    return false;
  }

  return dayjs(value).isAfter(dayjs(ceiling), dateOnly ? "day" : "second");
};

/** True when `end` is strictly before `start` (both present & valid). */
export const isEndBeforeStart = (
  start: string | null | undefined,
  end: string | null | undefined
): boolean => {
  if (!start || !end) return false;
  const s = dayjs(start);
  const e = dayjs(end);
  return s.isValid() && e.isValid() && e.isBefore(s);
};

/** Reformat a picker value between date-only and date-time shapes (on all-day toggle). */
export const normalizePickerValue = (
  value: string | null | undefined,
  allDay: boolean
): string | null => {
  if (!value) return null;
  const d = dayjs(value);
  if (!d.isValid()) return null;
  return d.format(allDay ? DATE_VALUE_FMT : DATETIME_VALUE_FMT);
};

export interface EventDurationInfo {
  /** Natural-language duration, e.g. "1 day 3 hours 15 minutes" or "2 hours". */
  text: string;
  /** The event crosses midnight but lasts under 24h (an overnight event). */
  isOvernight: boolean;
}

/**
 * Human-readable event duration. For timed events: "1 day 3 hours 15 minutes",
 * "2 hours", "45 minutes"; flags overnight when it crosses a day boundary but is
 * under 24h. For all-day events: whole-day count ("1 day", "3 days"). Returns
 * null for missing/invalid/zero-or-negative spans.
 *
 * `tz` — the zone whose midnight decides "overnight" and the all-day count. Pass the EVENT's (null → UTC) wherever the
 * result is rendered on a server too: without it the day boundary is the RUNTIME's, so a 10 PM–2 AM Toronto event was
 * "overnight" in a Toronto browser and not on the UTC server, and the public hero logged a hydration mismatch. Omitted →
 * the runtime's zone (the host app, client-rendered only).
 */
export const formatEventDuration = (
  start: string | null | undefined,
  end: string | null | undefined,
  allDay = false,
  tz?: string | null
): EventDurationInfo | null => {
  if (!start || !end) return null;
  const zone = tz === undefined ? null : String(tz ?? "").trim() || "UTC";
  const inZone = (v: string) => {
    if (!zone) {
      return dayjs(v);
    }

    try {
      return dayjs(v).tz(zone);
    } catch {
      return dayjs(v).tz("UTC");
    }
  };
  const s = inZone(start);
  const e = inZone(end);
  if (!s.isValid() || !e.isValid()) return null;

  if (allDay) {
    const days = e.startOf("day").diff(s.startOf("day"), "day") + 1;
    if (days <= 0) return null;
    return { text: days === 1 ? "1 day" : `${days} days`, isOvernight: false };
  }

  const totalMinutes = e.diff(s, "minute");
  if (totalMinutes <= 0) return null;

  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(days === 1 ? "1 day" : `${days} days`);
  if (hours > 0) parts.push(hours === 1 ? "1 hour" : `${hours} hours`);
  if (minutes > 0) parts.push(minutes === 1 ? "1 minute" : `${minutes} minutes`);

  return {
    text: parts.join(" ") || "0 minutes",
    isOvernight: totalMinutes < 1440 && !s.isSame(e, "day"),
  };
};

/**
 * Convert a date (string or Date) to local date string for DatePickerInput
 * DatePickerInput expects format "YYYY-MM-DD"
 */
export const toDatePickerString = (
  date: string | Date | null | undefined
): string | null => {
  if (!date) return null;
  try {
    const d = dayjs(date);
    if (!d.isValid()) return null;
    return d.format("YYYY-MM-DD");
  } catch {
    return null;
  }
};

/**
 * Convert DatePickerInput string (YYYY-MM-DD) to ISO string
 */
export const datePickerStringToIso = (
  dateStr: string | null | undefined
): string | null => {
  if (!dateStr) return null;
  try {
    const d = dayjs(dateStr);
    if (!d.isValid()) return null;
    return d.toISOString();
  } catch {
    return null;
  }
};

/**
 * Safely parse a date input to dayjs object
 * Returns null if invalid
 */
export const toDayjs = (
  date: string | Date | null | undefined
): dayjs.Dayjs | null => {
  if (!date) return null;
  const d = dayjs(date);
  return d.isValid() ? d : null;
};

/**
 * Calculate comparison period dates based on type and primary period.
 * Used for period-over-period comparisons in reports.
 * 
 * @param type - The comparison type
 * @param primaryFrom - Start date of the primary period (string or Date)
 * @param primaryTo - End date of the primary period (string or Date)
 * @param customFrom - Custom start date when type is "custom"
 * @returns Object with dateFrom and dateTo as ISO strings, or null values
 * 
 * @example
 * // Previous period (same length, immediately before)
 * getComparisonPeriodDates("previous", "2025-01-15", "2025-01-31")
 * // Returns: { dateFrom: "2024-12-30T...", dateTo: "2025-01-14T..." }
 * 
 * // Previous month (same dates shifted back one month)
 * getComparisonPeriodDates("previousMonth", "2025-02-01", "2025-02-28")
 * // Returns: { dateFrom: "2025-01-01T...", dateTo: "2025-01-28T..." }
 * 
 * // Same month last year
 * getComparisonPeriodDates("sameMonthLastYear", "2025-03-01", "2025-03-31")
 * // Returns: { dateFrom: "2024-03-01T...", dateTo: "2024-03-31T..." }
 */
export const getComparisonPeriodDates = (
  type: PeriodComparisonType,
  primaryFrom: string | Date | null | undefined,
  primaryTo: string | Date | null | undefined,
  customFrom?: string | Date | null
): { dateFrom: string | null; dateTo: string | null } => {
  const from = toDayjs(primaryFrom);
  const to = toDayjs(primaryTo);

  if (!from || !to) {
    return { dateFrom: null, dateTo: null };
  }

  const periodDays = to.diff(from, "day");

  switch (type) {
    case "previous": {
      // Previous period of same length, ending the day before primary starts
      const compTo = from.subtract(1, "day");
      const compFrom = compTo.subtract(periodDays, "day");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "previousMonth": {
      // Same dates shifted back one month
      const compFrom = from.subtract(1, "month");
      const compTo = to.subtract(1, "month");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "sameMonthLastYear": {
      // Same dates shifted back one year
      const compFrom = from.subtract(1, "year");
      const compTo = to.subtract(1, "year");
      return {
        dateFrom: compFrom.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "custom": {
      // Custom start date with same period length
      const customStart = toDayjs(customFrom);
      if (!customStart) {
        return { dateFrom: null, dateTo: null };
      }
      const compTo = customStart.add(periodDays, "day");
      return {
        dateFrom: customStart.toISOString(),
        dateTo: compTo.toISOString(),
      };
    }

    case "none":
    default:
      return { dateFrom: null, dateTo: null };
  }
};

// ─── Event-timezone datetime (UTC in DB ⇄ the event's local wall-clock) ────────
//
// The DB stores instants in UTC; the UI must show/edit them in the EVENT's timezone
// (an IANA string like "America/Toronto") so a slot at "10:00 AM" reads 10:00 AM to
// everyone regardless of where the viewer is. Mirrors the Timeline feature's approach.

/** The event's IANA timezone, falling back to the viewer's guess, then UTC. */
export const resolveEventTz = (tz?: string | null): string => {
  if (tz) {
    return tz;
  }

  try {
    return dayjs.tz.guess();
  } catch {
    return "UTC";
  }
};

/** A stored UTC instant → the event-local "YYYY-MM-DD HH:mm:ss" string a DateTimeField edits. */
export const utcToEventInput = (utcStr?: string | null, tz?: string | null): string | null => {
  if (!utcStr) {
    return null;
  }

  const d = dayjs.utc(utcStr);
  if (!d.isValid()) {
    return null;
  }

  return d.tz(resolveEventTz(tz)).format("YYYY-MM-DD HH:mm:ss");
};

/** An event-local picker string → a UTC ISO instant for the API. */
export const eventInputToUtc = (localStr?: string | null, tz?: string | null): string | null => {
  if (!localStr) {
    return null;
  }

  const d = dayjs.tz(localStr, resolveEventTz(tz));
  if (!d.isValid()) {
    return null;
  }

  return d.utc().toISOString();
};

/** A single UTC instant → "MMM D, YYYY · h:mm A" in the event's timezone. */
export const formatEventDateTime = (utcStr?: string | null, tz?: string | null): string => {
  if (!utcStr) {
    return "";
  }

  const d = dayjs.utc(utcStr);
  if (!d.isValid()) {
    return "";
  }

  return d.tz(resolveEventTz(tz)).format("MMM D, YYYY · h:mm A");
};

/**
 * A start (+ optional end) range in the event's timezone. Same-day collapses the date
 * ("Aug 1, 2026 · 2:00 – 3:30 PM"); a cross-day range shows both ends.
 */
export const formatEventDateTimeRange = (startUtc?: string | null, endUtc?: string | null, tz?: string | null): string => {
  if (!startUtc) {
    return "";
  }

  const zone = resolveEventTz(tz);
  const start = dayjs.utc(startUtc);
  if (!start.isValid()) {
    return "";
  }

  const zStart = start.tz(zone);

  if (!endUtc) {
    return zStart.format("MMM D, YYYY · h:mm A");
  }

  const end = dayjs.utc(endUtc);
  if (!end.isValid()) {
    return zStart.format("MMM D, YYYY · h:mm A");
  }

  const zEnd = end.tz(zone);

  if (zStart.isSame(zEnd, "day")) {
    return `${zStart.format("MMM D, YYYY · h:mm A")} – ${zEnd.format("h:mm A")}`;
  }

  return `${zStart.format("MMM D, YYYY · h:mm A")} – ${zEnd.format("MMM D, YYYY · h:mm A")}`;
};

/**
 * A start (+ optional end) split into a non-wrapping { date, time } pair for a compact
 * two-line column (avoids truncating a long "date · time – time" range). Event timezone.
 * Same-day collapses to one date; a cross-day range carries both dates in `date`.
 */
export const formatEventWhenParts = (startUtc?: string | null, endUtc?: string | null, tz?: string | null): { date: string; time: string } | null => {
  if (!startUtc) {
    return null;
  }

  const zone = resolveEventTz(tz);
  const start = dayjs.utc(startUtc);
  if (!start.isValid()) {
    return null;
  }

  const zStart = start.tz(zone);

  if (!endUtc) {
    return { date: zStart.format("MMM D, YYYY"), time: zStart.format("h:mm A") };
  }

  const end = dayjs.utc(endUtc);
  if (!end.isValid()) {
    return { date: zStart.format("MMM D, YYYY"), time: zStart.format("h:mm A") };
  }

  const zEnd = end.tz(zone);

  if (zStart.isSame(zEnd, "day")) {
    return { date: zStart.format("MMM D, YYYY"), time: `${zStart.format("h:mm A")} – ${zEnd.format("h:mm A")}` };
  }

  return { date: `${zStart.format("MMM D")} – ${zEnd.format("MMM D, YYYY")}`, time: `${zStart.format("h:mm A")} – ${zEnd.format("h:mm A")}` };
};

/** Short timezone label for the given instant (e.g. "EDT"), falling back to the IANA name. */
export const eventTzAbbrev = (tz?: string | null, at?: string | null): string => {
  const zone = resolveEventTz(tz);

  try {
    const dtf = new Intl.DateTimeFormat("en-US", { timeZone: zone, timeZoneName: "short" });
    const part = dtf.formatToParts(at ? new Date(at) : new Date()).find((p) => p.type === "timeZoneName");
    return part?.value || zone;
  } catch {
    return zone;
  }
};